import {
    Button,
    Typography,
    Stack,
    Box,
    Divider,
    Paper,
    CircularProgress,
    Alert,
} from "@mui/material";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCheckout } from "../CheckoutContext";
import { useAuth } from "../../context/AuthContext";
import { ref, push, update, get } from "firebase/database";
import { rtdb } from "../../services/firebase";

function InfoRow({ label, value }) {
    if (!value) return null;
    return (
        <Stack direction="row" justifyContent="space-between">
            <Typography color="var(--cinza)">{label}</Typography>
            <Typography fontWeight={700}>{value}</Typography>
        </Stack>
    );
}

function ReviewCard({ title, children }) {
    return (
        <Paper
            variant="outlined"
            sx={{ p: 2.5, borderRadius: 2 }}
        >
            <Typography fontWeight={600} mb={1}>
                {title}
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
            <Stack spacing={1}>{children}</Stack>
        </Paper>
    );
}

export default function ReviewStep() {
    const { checkoutData, prevStep, items, finalizarCheckout, paymentStatus, setActiveStep, subtotal, totalAmount, creditCalc } = useCheckout();
    const { user, clearUserCart } = useAuth();
    const navigate = useNavigate();
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const { client, address, shipping, payment } = checkoutData;

    async function finish() {
        try {
          setLoading(true);
          setError(null);

          // Crédito via link de pagamento: abre WhatsApp e não avança nem limpa carrinho
          if (payment.method === "credit_link") {
            await finalizarCheckout();
            return;
          }

          // Se PIX, vai para tela do QR Code
          if (payment.method === "pix") {
            await finalizarCheckout();
            setActiveStep((prev) => prev + 1);
          } else {
            await finalizarCheckout();
          }

          // Se cartão, o status já vem como PAID ou FAILED
        } catch (err) {
          console.error(err);
          setError("Erro ao finalizar pagamento. Tente novamente.");
        } finally {
          setLoading(false);
        }
    }

    async function handleFinalizar() {
        
        if (!items || items.length === 0) {
            setError("Carrinho vazio");
            return;
        }

        setLoading(true);
        setError("");

        try {
            // Calcular totais
            const subtotal = items.reduce(
                (sum, item) => sum + item.price * item.quantity,
                0
            );
            const total = subtotal + (shipping.price || 0);

            let realPay = payment.method === "credit" ? "card" : "pix";

            const realCep = (address.cep.replace(/\D/g, "")).slice(0, 8);

            // Criar pedido
            const orderData = {
                userId: user?.uid || null,
                status: "paid",
                client: {
                    name: client.name,
                    email: client.email,
                    phone: client.phone,
                    cpf: client.cpf,
                },
                address: {
                    cep: realCep,
                    street: address.street,
                    number: address.number,
                    complement: address.complement || "",
                    district: address.district || "",
                    city: address.city || "",
                    state: address.state || "",
                },
                shipping: {
                    method: shipping.method,
                    price: shipping.price || 0,
                    prazo: shipping.prazo || "",
                    carrier: shipping.carrier || "",
                },
                payment: {
                    method: realPay,
                    installments: payment.installments || 1,
                    card: payment.card || null,
                },
                items: items,
                subtotal,
                shippingPrice: shipping.price || 0,
                total,
            };

            // Salvar endereço como padrão se solicitado
            if (user?.uid && address.saveAsDefault) {
                try {
                    const clientPath = `users/client/${user.uid}`;
                    const addressesRef = ref(rtdb, `${clientPath}/addresses`);
                    
                    const addressPayload = {
                        label: "Casa",
                        recipientName: client.name || "",
                        phone: client.phone || "",
                        street: address.street || "",
                        number: address.number || "",
                        complement: address.complement || "",
                        neighborhood: address.district || "",
                        city: address.city || "",
                        state: address.state || "",
                        zipCode: address.cep?.replace(/\D/g, "") || "",
                        country: "BR",
                        referencePoint: "",
                        isDefault: true,
                    };

                    // Verificar se já existem endereços para remover isDefault dos outros
                    const userSnap = await get(ref(rtdb, clientPath));
                    if (userSnap.exists()) {
                        const userData = userSnap.val();
                        if (userData.addresses) {
                            const updates = {};
                            Object.keys(userData.addresses).forEach(addrId => {
                                if (userData.addresses[addrId].isDefault) {
                                    updates[`${clientPath}/addresses/${addrId}/isDefault`] = false;
                                }
                            });
                            if (Object.keys(updates).length > 0) {
                                await update(ref(rtdb, "/"), updates);
                            }
                        }
                    }

                    // Criar novo endereço
                    const newAddressRef = push(addressesRef);
                    await update(newAddressRef, addressPayload);
                } catch (err) {
                    console.error("Erro ao salvar endereço padrão:", err);
                    // Não bloqueia o pedido se falhar ao salvar endereço
                }
            }

            // Limpar carrinho
            clearUserCart();
            
            // Redirecionar para página de sucesso ou home
            setTimeout(() => {
                navigate("/meus-pedidos", { 
                    state: { 
                        orderSuccess: true,
                    } 
                });
            }, 4000);

        } catch (err) {
            console.error("Erro ao finalizar pedido:", err);
            setError("Erro ao finalizar pedido. Tente novamente.");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {

        if (paymentStatus === "PAID") {
            setTimeout(() => {
                handleFinalizar();
            }, 1500);
        } else if (paymentStatus === "FAILED") {
            setError("Erro ao finalizar pedido. Tente novamente.");
        }

    }, [paymentStatus]);

    let realMethod =
        payment.method === "credit" ? "Cartão de crédito" :
        payment.method === "credit_link" ? "Crédito - Link de pagamento" :
        "PIX";

    return (
        <Stack spacing={3}>
            <Typography variant="h6">
                Revisão final do pedido
            </Typography>

            {/* CLIENTE */}
            <ReviewCard title="Dados do cliente">
                <InfoRow label="Nome" value={client.name}/>
                <InfoRow label="E-mail" value={client.email} />
                <InfoRow label="Telefone" value={client.phone} />
                <InfoRow label="CPF" value={client.cpf} />
            </ReviewCard>

            {/* ENDEREÇO */}
            <ReviewCard title="Endereço de entrega">
                <InfoRow
                    label="Endereço"
                    value={`${address.street}, ${address.number}`}
                />
                <InfoRow label="CEP" value={address.cep} />
                {address.complement && (
                    <InfoRow
                        label="Complemento"
                        value={address.complement}
                    />
                )}
            </ReviewCard>

            {/* FRETE */}
            <ReviewCard title="Frete">
                <InfoRow label="Transportadora" value={shipping.carrier} />
                <InfoRow label="Prazo" value={shipping.prazo} />
                <InfoRow
                    label="Valor"
                    value={`R$ ${shipping.price.toFixed(2)}`}
                />
            </ReviewCard>

            {/* PAGAMENTO */}
            <ReviewCard title="Pagamento">
                <InfoRow
                    label="Forma"
                    value={realMethod}
                />
                {payment.method === "credit" && (
                    <>
                        <InfoRow
                            label="Parcelamento"
                            value={`${payment.installments}x`}
                        />
                        <InfoRow
                            label="Juros do crédito"
                            value={`R$ ${creditCalc.feeValue.toFixed(2)}`}
                        />
                    </>
                )}
            </ReviewCard>

            {/* ITENS DO PEDIDO */}
            {items && items.length > 0 && (
                <ReviewCard title="Itens do pedido">
                    {items.map((item, index) => (
                        <Box key={index} sx={{ mb: 1 }}>
                            <InfoRow
                                label={`${item.productName} - ${item.flavor} (${item.quantity}x)`}
                                value={`R$ ${(item.price * item.quantity).toFixed(2)}`}
                            />
                        </Box>
                    ))}
                </ReviewCard>
            )}

            {/* RESUMO DE VALORES */}
            <ReviewCard title="Resumo">
                <InfoRow
                    label="Subtotal"
                    value={`R$ ${(totalAmount - shipping.price || 0).toFixed(2)}`}
                />
                <InfoRow
                    label="Frete"
                    value={`R$ ${(shipping.price || 0).toFixed(2)}`}
                />
                <InfoRow
                    label="Total"
                    value={`R$ ${totalAmount.toFixed(2)}`}
                />
            </ReviewCard>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* AÇÕES */}
            <Stack direction="row" spacing={2} mt={2}>
                <Button
                    variant="outlined"
                    onClick={prevStep}
                    fullWidth
                    disabled={loading}
                >
                    Voltar
                </Button>

                <Button
                    variant="contained"
                    onClick={finish}
                    fullWidth
                    disabled={loading}
                >
                    {loading ? (
                        <>
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                            Finalizando...
                        </>
                    ) : (
                        payment.method === "credit_link" ? "Enviar pedido via WhatsApp" : "Finalizar Pedido"
                    )}
                </Button>
            </Stack>
        </Stack>
    );
}
